import { contract, errorCodeSchema } from '@iac/contracts';
import type { ErrorCode } from '@iac/contracts';
import { initClient, tsRestFetchApi } from '@ts-rest/core';

/**
 * The access token lives in module scope — never `localStorage`. An XSS can
 * still use it while the page is open, but it cannot exfiltrate something that
 * outlives the tab, and the refresh token it would need to mint more is in an
 * httpOnly cookie it cannot read at all.
 */
let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

type Listener = () => void;
const unauthenticatedListeners = new Set<Listener>();

/** Fired when a refresh fails, i.e. the session is genuinely over. */
export function onUnauthenticated(listener: Listener): () => void {
  unauthenticatedListeners.add(listener);
  return () => unauthenticatedListeners.delete(listener);
}

function notifyUnauthenticated(): void {
  setAccessToken(null);
  for (const listener of unauthenticatedListeners) listener();
}

let inFlightRefresh: Promise<boolean> | null = null;

/**
 * Single-flight: a page that fires five queries at once and gets five 401s must
 * produce **one** refresh, not five. Five would rotate the token five times and
 * the losers would replay a rotated token — which the server correctly reads as
 * theft and answers by killing the whole session.
 */
export function refreshAccessToken(): Promise<boolean> {
  inFlightRefresh ??= (async () => {
    try {
      const res = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { accessToken: string };
      setAccessToken(body.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

function withAuthHeader(headers: Record<string, string>) {
  return accessToken
    ? { ...headers, authorization: `Bearer ${accessToken}` }
    : headers;
}

/**
 * These three manage credentials themselves; retrying them would loop. Every
 * other `/api/auth/*` route (`me`, `password`, `locale`) is an ordinary guarded
 * call and gets the refresh-and-replay like any other.
 */
const CREDENTIAL_ROUTES = [
  '/api/auth/login',
  '/api/auth/refresh',
  '/api/auth/logout',
];

function isAuthRoute(path: string): boolean {
  return CREDENTIAL_ROUTES.some((route) => path.startsWith(route));
}

export const api = initClient(contract, {
  baseUrl: '',
  credentials: 'same-origin',
  api: async (args) => {
    const response = await tsRestFetchApi({
      ...args,
      headers: withAuthHeader(args.headers),
    });

    if (response.status !== 401 || isAuthRoute(args.path)) return response;

    // The access token is short-lived by design, so a 401 mid-session is the
    // expected case, not an error: refresh once and replay the request.
    const refreshed = await refreshAccessToken();
    if (!refreshed) {
      notifyUnauthenticated();
      return response;
    }

    const retried = await tsRestFetchApi({
      ...args,
      headers: withAuthHeader(args.headers),
    });
    if (retried.status === 401) notifyUnauthenticated();
    return retried;
  },
});

/**
 * Thrown by the query/mutation wrappers so TanStack Query sees a rejection.
 * `code` is the server's reason, which `toastError` translates; `message` is
 * the calling screen's own (already translated) failure text, shown when the
 * code is missing. The server's `message` is diagnostics and never lands here.
 */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: ErrorCode | null,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** The contract's error code from a body, if it carries a known one. */
function errorCode(body: unknown): ErrorCode | null {
  if (body && typeof body === 'object' && 'code' in body) {
    const parsed = errorCodeSchema.safeParse(body.code);
    if (parsed.success) return parsed.data;
  }
  return null;
}

/**
 * Collapse a ts-rest response union to the success body, or throw so TanStack
 * Query sees a rejection. `fallback` is the screen's generic failure text, used
 * when the body has no code the UI knows (a 500, say).
 */
export function unwrap<
  R extends { status: number; body: unknown },
  S extends R['status'],
>(res: R, ok: S, fallback: string): Extract<R, { status: S }>['body'] {
  if (res.status !== ok) {
    throw new ApiError(res.status, errorCode(res.body), fallback);
  }
  return res.body;
}
